import { ListItemText } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import type { DynamicInvoiceIdType } from '@vy/api-definition';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { buildCurrencyLevel, buildEmptyRevenueBreakdown } from '@vy/services';
import { INTEGRATION_PROVIDERS, INVOICE_REVIEW_STATUSES, RevenueBreakdown } from '@vy/types';
import {
  isNotEmptyArray,
  isNotNullOrUndefined,
  isNullOrUndefined,
  sumBy,
  validateNotEmptyString,
  validateNotNullOrUndefined,
  values,
} from '@vy/utils';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useAnrokEnabled } from '../../../hooks/requests/integrations/anrok/use-anrok-enabled';
import { prefetchNetSuiteClasses } from '../../../hooks/requests/integrations/net-suite/use-net-suite-classes';
import { useIntegrationPrefetch } from '../../../hooks/requests/integrations/use-conditional-integration-prefetch';
import { useDynamicInvoice } from '../../../hooks/requests/use-dynamic-invoice';
import { useParamsDynamicInvoiceIdOrDefault } from '../../../hooks/use-dynamic-invoice-id';
import { useLineItemsSplitter } from '../../../hooks/use-line-items-splitter';
import { queryKeys } from '../../../services/query-key/query-keys';
import { DynamicInvoiceContextProvider } from './dynamic-invoice-context';
import { AddRevisionedLineItemButton } from './dynamic-invoice-view-components/AddRevisionedLineItemButton';
import { DynamicInvoiceHeader } from './dynamic-invoice-view-components/dynamic-invoice-header/DynamicInvoiceHeader';
import { PromotionLineItemsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/PromotionLineItemsSection';
import { RevisionedLineItemsDialogsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/RevisionedLineItemsDialogsSection';
import { DynamicInvoiceSummary } from './dynamic-invoice-view-components/DynamicInvoiceSummary';
import { DynamicInvoiceViewPageInvoiceNotFound } from './dynamic-invoice-view-components/DynamicInvoiceViewPageInvoiceNotFound';
import type { DialogsState } from './dynamic-invoice-view-components/revisioned-line-item-dialog-types/dialogs-state';
import type { RevisionedLineItemDialogType } from './dynamic-invoice-view-components/revisioned-line-item-dialog-types/revisioned-line-item-dialog-type';
import { RevisionedLineItemDialogs } from './dynamic-invoice-view-components/revisioned-line-item-dialog-types/revisioned-line-item-dialog-type';
import { UsageLinesContainer } from './usage-lines-section/containers/usage-lines-container';

type DynamicInvoiceViewPageProps = {
  id?: string;
  idType: DynamicInvoiceIdType;
  noRedirectOnApprove?: boolean;
};

const DynamicInvoiceViewPage: React.FC<DynamicInvoiceViewPageProps> = ({
  id,
  idType,
  noRedirectOnApprove = false,
}) => {
  const defaultDynamicInvoiceId = useParamsDynamicInvoiceIdOrDefault(id);

  useIntegrationPrefetch(INTEGRATION_PROVIDERS.netSuite, prefetchNetSuiteClasses);
  const dynamicInvoiceId = isNotNullOrUndefined(id) ? id : defaultDynamicInvoiceId;

  validateNotEmptyString(dynamicInvoiceId, 'dynamicInvoiceId');

  const { data: dynamicInvoiceDataResponse } = useDynamicInvoice(dynamicInvoiceId, idType);

  validateNotNullOrUndefined(dynamicInvoiceDataResponse, 'dynamicInvoiceDataResponse');

  const { dynamicInvoiceData } = dynamicInvoiceDataResponse;
  const customerSettings = dynamicInvoiceData.customer.settings;
  const dynamicInvoiceQueryKey = queryKeys.dynamicInvoice(dynamicInvoiceId);

  const isAnrokEnabled = useAnrokEnabled();

  const { lineItems, revenueBreakdown } = dynamicInvoiceData.invoice;
  const isTaxIncludedInTotal =
    isAnrokEnabled && isNotNullOrUndefined(dynamicInvoiceData.invoice.refundsInvoiceId);
  const { promotionLineItems } = useLineItemsSplitter(lineItems);

  const revisionedLineItems = lineItems.filter((lineItem) => lineItem.isRevised);

  const [dialogsState, setDialogsState] = useState<DialogsState>({
    [RevisionedLineItemDialogs.Fixed]: {
      open: false,
      defaultValues: {
        revenueBreakdown: buildEmptyRevenueBreakdown(),
      },
    },
    [RevisionedLineItemDialogs.Promotion]: {
      open: false,
      defaultValues: {
        revenueBreakdown: buildEmptyRevenueBreakdown(),
      },
    },
  });

  const handleAddRevisionedItemSelect = (revisionedLineItemDialogType: RevisionedLineItemDialogType) => {
    setDialogsState((previousState) => ({
      ...previousState,
      [revisionedLineItemDialogType]: {
        ...previousState[revisionedLineItemDialogType],
        lineItemId: undefined,
        defaultValues: undefined,
        open: true,
      },
    }));
  };

  const isApproved = dynamicInvoiceData.invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;

  const invoiceName = dynamicInvoiceData.invoice.name;
  const contractCurrency = buildCurrencyLevel({
    invoiceCurrency: dynamicInvoiceData.invoice.baseCurrency,
    contractCurrency: dynamicInvoiceData.contract?.currencyOverride,
    customerCurrency: customerSettings?.currency,
  });
  const currency = buildCurrencyLevel({
    invoiceCurrency: dynamicInvoiceData.invoice.currency,
    customerCurrency: customerSettings?.currency,
  });

  const creditProjects =
    isNullOrUndefined(dynamicInvoiceDataResponse.dynamicInvoiceData.creditPoolsProjectionMap) || isApproved
      ? []
      : values(dynamicInvoiceDataResponse.dynamicInvoiceData.creditPoolsProjectionMap);

  const creditsUsed = sumBy(creditProjects, 'projectedDeduction');
  const balanceRemaining = creditProjects[0]?.projectedBalance;

  const revenueBreakdownWithCredits: RevenueBreakdown = isApproved
    ? revenueBreakdown
    : {
        ...revenueBreakdown,
        creditsUsed,
        total: revenueBreakdown.total - creditsUsed,
      };

  return (
    <>
      <Helmet>
        <title>{`Invoice ${invoiceName} ${dynamicInvoiceData.customer.name}`}</title>
      </Helmet>
      <DynamicInvoiceContextProvider dynamicInvoiceDataResponse={dynamicInvoiceDataResponse}>
        <Stack
          sx={{
            margin: 'auto',
            maxWidth: '1200px',
            paddingTop: '20px',
          }}
        >
          <DynamicInvoiceHeader
            dynamicInvoiceDataResponse={dynamicInvoiceDataResponse}
            currency={currency}
            contractCurrency={contractCurrency}
            queryKeyToInvalidate={dynamicInvoiceQueryKey}
            noRedirectOnApprove={noRedirectOnApprove}
          />
          <Stack
            spacing={5}
            sx={{
              paddingTop: 2,
            }}
          >
            <UsageLinesContainer />

            {isNotEmptyArray(promotionLineItems) && (
              <PromotionLineItemsSection promotionLineItems={promotionLineItems} />
            )}

            {!isApproved && (
              <AddRevisionedLineItemButton>
                <MenuItem
                  {...getSelectorAttribute(dynamicInvoicePageSelectors.revision.revisionFixedFee)}
                  onClick={() => handleAddRevisionedItemSelect(RevisionedLineItemDialogs.Fixed)}
                >
                  <ListItemText>Fixed fee</ListItemText>
                </MenuItem>

                <MenuItem
                  {...getSelectorAttribute(dynamicInvoicePageSelectors.revision.revisionFixedDiscount)}
                  disabled={isNotEmptyArray(revisionedLineItems)}
                  onClick={() => handleAddRevisionedItemSelect(RevisionedLineItemDialogs.Promotion)}
                >
                  <ListItemText>Discount</ListItemText>
                </MenuItem>
              </AddRevisionedLineItemButton>
            )}

            <RevisionedLineItemsDialogsSection
              invoiceId={dynamicInvoiceData.invoice.id}
              dialogsState={dialogsState}
              setDialogsState={setDialogsState}
              lineItems={revisionedLineItems}
              promotionLineItems={promotionLineItems}
              queryKeyToInvalidate={dynamicInvoiceQueryKey}
            />

            <DynamicInvoiceSummary
              revenueBreakdown={revenueBreakdownWithCredits}
              currency={currency}
              taxRate={customerSettings?.taxRate ?? 0}
              isTaxIncludedInTotal={isTaxIncludedInTotal}
              remainingCreditBalanceAfterApproval={balanceRemaining}
            />
          </Stack>
        </Stack>
      </DynamicInvoiceContextProvider>
    </>
  );
};

const DynamicInvoiceViewPageWithSuspenseBoundary = withGlobalSearchParams(
  withSuspenseBoundary(DynamicInvoiceViewPage, {
    errorBoundaryProps: {
      fallback: <DynamicInvoiceViewPageInvoiceNotFound />,
    },
  }),
);

export { DynamicInvoiceViewPageWithSuspenseBoundary as DynamicInvoiceViewPage };
